import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Smartphone, RefreshCw, Search, Send, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatWhen = (iso) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
};

export const AppAdoptionPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('notif'); // 'notif' | 'installed'
    const [q, setQ] = useState('');
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkTitle, setBulkTitle] = useState('');
    const [bulkMsg, setBulkMsg] = useState('');
    const [sending, setSending] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/app-adoption`, { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error('Could not load app-adoption data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const list = useMemo(() => {
        if (!data) return [];
        const src = tab === 'notif' ? (data.notif_players || []) : (data.installed_players || []);
        const term = q.trim().toLowerCase();
        if (!term) return src;
        return src.filter(p =>
            (p.name || '').toLowerCase().includes(term) ||
            (p.email || '').toLowerCase().includes(term) ||
            (p.phone || '').toLowerCase().includes(term));
    }, [data, tab, q]);

    const notifIds = useMemo(() => (data?.notif_players || []).map(p => p.id).filter(Boolean), [data]);

    const sendBulk = async () => {
        if (!bulkMsg.trim() || notifIds.length === 0) return;
        setSending(true);
        try {
            const { data: res } = await axios.post(`${API}/admin/message/broadcast`, {
                user_ids: notifIds,
                cohort: 'Notifications-On',
                title: bulkTitle.trim() || 'Betohalics',
                message: bulkMsg.trim(),
            }, { withCredentials: true });
            toast.success(`Sent to ${res.sent_count ?? notifIds.length} players`);
            setBulkOpen(false); setBulkTitle(''); setBulkMsg('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not send message');
        } finally {
            setSending(false);
        }
    };

    const Tile = ({ icon: Icon, color, value, label, sub }) => (
        <div className="flex-1 rounded-xl border p-3.5 min-w-[140px]"
             style={{ borderColor: `${color}44`, background: `${color}0d` }}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[11px] font-bold uppercase tracking-wide text-[#A0AAB2]">{label}</span>
            </div>
            <p className="text-3xl font-black leading-none" style={{ color }}>{value}</p>
            {sub && <p className="text-[10px] text-[#A0AAB2] mt-1">{sub}</p>}
        </div>
    );

    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden" data-testid="app-adoption-panel">
            <button onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                data-testid="app-adoption-toggle">
                <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-sm font-black text-white">App Adoption</span>
                    {data && (
                        <span className="text-[11px] text-[#A0AAB2]">
                            · <span className="text-[#39FF14] font-bold">{data.notif_on_count}</span> notify · <span className="text-[#00E5FF] font-bold">{data.installed_count}</span> installed
                        </span>
                    )}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-[#A0AAB2]" /> : <ChevronDown className="w-4 h-4 text-[#A0AAB2]" />}
            </button>

            {open && (
                <div className="px-4 pb-4">
                    {loading && !data ? (
                        <div className="py-8 text-center text-[#A0AAB2] text-sm">Loading…</div>
                    ) : data ? (
                        <>
                            <div className="flex flex-wrap gap-3 mb-4">
                                <Tile icon={Bell} color="#39FF14" value={data.notif_on_count} label="Notifications On"
                                      sub={`${data.notif_device_count} device${data.notif_device_count === 1 ? '' : 's'}`} />
                                <Tile icon={Smartphone} color="#00E5FF" value={data.installed_count} label="Home-Screen Installs" />
                                <Tile icon={Smartphone} color="#FFD700" value={data.total_users} label="Total Players" />
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between mb-3">
                                <div className="flex items-center gap-1 bg-[#0a0a0a] border border-white/10 rounded-lg p-1 w-fit" data-testid="app-adoption-tabs">
                                    <button onClick={() => setTab('notif')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition"
                                        style={{ background: tab === 'notif' ? '#39FF1422' : 'transparent', color: tab === 'notif' ? '#39FF14' : '#A0AAB2' }}
                                        data-testid="app-adoption-tab-notif">
                                        <Bell className="w-3.5 h-3.5" /> Notifications On <span className="text-[10px] font-mono opacity-80">{data.notif_on_count}</span>
                                    </button>
                                    <button onClick={() => setTab('installed')}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition"
                                        style={{ background: tab === 'installed' ? '#00E5FF22' : 'transparent', color: tab === 'installed' ? '#00E5FF' : '#A0AAB2' }}
                                        data-testid="app-adoption-tab-installed">
                                        <Smartphone className="w-3.5 h-3.5" /> Installed <span className="text-[10px] font-mono opacity-80">{data.installed_count}</span>
                                    </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    {tab === 'notif' && notifIds.length > 0 && (
                                        <Button size="sm" onClick={() => setBulkOpen(true)}
                                            className="bg-[#39FF14] text-black font-bold" data-testid="app-adoption-message-all">
                                            <MessageCircle className="w-4 h-4 mr-1" /> Message all {notifIds.length}
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}
                                        className="border-white/15 text-[#A0AAB2] hover:text-white" data-testid="app-adoption-refresh">
                                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                            </div>

                            <div className="relative mb-2">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search name, email, or phone…"
                                    className="pl-9 bg-[#0a0a0a] border-white/10 text-white h-9 text-sm"
                                    data-testid="app-adoption-search" />
                            </div>

                            <div className="max-h-80 overflow-y-auto rounded-lg border border-white/5 divide-y divide-white/5" data-testid="app-adoption-list">
                                {list.length === 0 ? (
                                    <div className="py-8 text-center text-[#A0AAB2] text-sm">No players found.</div>
                                ) : list.map(p => (
                                    <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`app-adoption-row-${p.id}`}>
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-white truncate">
                                                {p.name}
                                                {p.role && p.role !== 'user' && (
                                                    <span className="ml-1.5 text-[9px] uppercase font-mono px-1 rounded bg-white/10 text-[#FFD700]">{p.role}</span>
                                                )}
                                            </p>
                                            <p className="text-[11px] text-[#A0AAB2] truncate">{p.email || p.phone || '—'}</p>
                                        </div>
                                        {tab === 'installed' && p.installed_at && (
                                            <span className="text-[10px] text-[#A0AAB2] shrink-0">{formatWhen(p.installed_at)}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="py-8 text-center text-[#A0AAB2] text-sm">No data.</div>
                    )}
                </div>
            )}

            {bulkOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" data-testid="app-adoption-message-modal"
                     onClick={(e) => { if (e.target === e.currentTarget && !sending) setBulkOpen(false); }}>
                    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3">
                        <div>
                            <h3 className="text-lg font-black text-white">Message players with notifications on</h3>
                            <p className="text-xs text-[#A0AAB2]">Push + in-app notification to all <span className="text-white font-bold">{notifIds.length}</span> engaged players.</p>
                        </div>
                        <Input value={bulkTitle} onChange={(e) => setBulkTitle(e.target.value)} maxLength={80}
                            placeholder="Title (e.g. 🔥 Tonight only!)" className="bg-[#0a0a0a] border-white/10 text-white" data-testid="app-adoption-msg-title" />
                        <textarea value={bulkMsg} onChange={(e) => setBulkMsg(e.target.value)} maxLength={500} rows={4}
                            placeholder="e.g. New game just dropped — tap in and grab your bonus!"
                            className="w-full rounded-lg bg-[#0a0a0a] border border-white/10 text-white text-sm p-3 resize-none" data-testid="app-adoption-msg-body" />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={sending} className="border-white/15 text-white" data-testid="app-adoption-msg-cancel">Cancel</Button>
                            <Button onClick={sendBulk} disabled={sending || !bulkMsg.trim()} className="bg-[#39FF14] text-black font-black" data-testid="app-adoption-msg-send">
                                <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : `Send to ${notifIds.length}`}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppAdoptionPanel;
